import { ReactElement, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatchType, actions, ProfileCategory, Leader } from '../../store';
import { getProfileCategoryTemplate } from '../../store/templates';
import { PROFILE_CATEGORY_MAX_LENGTH, PAGE_URL, EVENT_TYPE } from '../../constants';
import { trackEvent } from '../../utils/appAnalyticsUtils';
import { useConfirmationModalWithUI } from '../Common/ConfirmationModal';
import IconPicker from '../Pickers/IconPicker';
import { CabinetIconData, CABINET_ICONS_LIST } from '../../icons';
import { CabModal } from '@CabComponents/CabModal';
import { CabButton, CabTextInput } from '@CabComponents';
import { useNavigate } from 'react-router';
import { Box, FormControl, FormLabel, Typography } from '@mui/material';
import colors from '../../colors';

interface ComponentProps {
  leader: Leader;
  category?: ProfileCategory;
  isOpen: boolean;
  onDidDismiss: () => void;
}

type Props = ComponentProps;

export const EditProfileCategory = ({
  leader, category, isOpen, onDidDismiss
}: Props): ReactElement => {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const { confirm: confirmDelete, ConfirmationModalComponent } = useConfirmationModalWithUI();
  const [editedCategory, setEditedCategory] = useState({
    ...(category || getProfileCategoryTemplate()),
    leader: leader.id,
  });
  const dispatch = useDispatch<ThunkDispatchType>();
  const createProfileCategory = useCallback((newCategory: ProfileCategory) => 
    dispatch(actions.profileNotes.createProfileCategory(newCategory)), [dispatch]);
  const updateProfileCategory = useCallback((updatedCategory: ProfileCategory) => 
    dispatch(actions.profileNotes.updateProfileCategory(updatedCategory)), [dispatch]);
  const deleteProfileCategory = useCallback((deletedCategory: ProfileCategory) => 
    dispatch(actions.profileNotes.deleteProfileCategory(deletedCategory)), [dispatch]);

  const navigate = useNavigate();

  useEffect(() => {
    setEditedCategory(category || { ...getProfileCategoryTemplate(), leader: leader.id });
  }, [category, isOpen, leader]);

  const handleSave = (): void => {
    setButtonDisabled(true);
    let saveFunc = createProfileCategory;
    let eventName = EVENT_TYPE.CREATE_EXEC_CATEGORY;
    if (editedCategory.id > -1) {
      saveFunc = updateProfileCategory;
      eventName = EVENT_TYPE.UPDATE_EXEC_CATEGORY;
    }
    saveFunc(editedCategory).then(() => {
      setButtonDisabled(false);
      trackEvent(eventName);
      onDidDismiss();
    });
  };

  const handleDelete = (): void => {
    deleteProfileCategory(editedCategory).then(() => {
      trackEvent(EVENT_TYPE.DELETE_EXEC_CATEGORY);
      navigate(`${PAGE_URL.EXECUTIVE}/${leader.id}`);
    });
  };

  const handleDeleteWithConfirmation = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this category? This cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      isDestructive: true,
      doubleConfirm: {
        title: "Are you sure you want to delete this category?",
        message: "All of the information you've stored will be deleted and unrecoverable.",
        confirmText: "Yes, Delete Category",
        cancelText: "Cancel"
      },
    });
    
    if (confirmed) {
      handleDelete();
    }
  };

  const handleSelectIcon = (icon: CabinetIconData): void => {
    setEditedCategory(prev => ({
      ...prev,
      icon_url: icon.icon_url,
    }));
    setIconPickerOpen(false);
  };

  const handleChangeTitle = (newTitle: string | null | undefined): void => {
    setEditedCategory(prev => ({...prev, title: newTitle?.slice(0, PROFILE_CATEGORY_MAX_LENGTH) || ''}));
  };

  return (
    <>
      <CabModal
        open={isOpen}
        onClose={onDidDismiss}
        title={`${editedCategory.id > -1 ? 'Edit' : 'Add'} Category`}
        closeIcon={true}
        closeOnBackdropClick={true}
        actionButtons={
          <>
            <CabButton buttonType='secondary' onClick={onDidDismiss} disabled={buttonDisabled}>
              Cancel
            </CabButton>
            {editedCategory.id > -1 && 
            <CabButton 
              buttonType="tertiary" 
              color="error" 
              onClick={handleDeleteWithConfirmation}
              disabled={!leader.permissions.categories[editedCategory.id]?.delete}
            >
              Delete Category
            </CabButton>
            }
            <CabButton buttonType='primary' onClick={handleSave} disabled={buttonDisabled || !editedCategory.title}>
              Save
            </CabButton>
          </>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            paddingTop: 2
          }}>
          <CabButton buttonType='text' sx={{height: '100%', padding: 3}}
            onClick={() => setIconPickerOpen(!iconPickerOpen)}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}>
              <Box component='img' src={editedCategory.icon_url} sx={{
                height: 64
              }} />
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: colors.navyDark
                }}>Change Icon</Typography>
            </Box>
          </CabButton>

          <FormControl fullWidth>
            <FormLabel>Page Title</FormLabel>
            <CabTextInput
              value={editedCategory.title}
              onChange={(e) => handleChangeTitle(e.target.value)}
              placeholder='Category Title'
            />
          </FormControl>
        </Box>
      </CabModal>
      <IconPicker
        showModal={iconPickerOpen}
        icons={CABINET_ICONS_LIST.filter(i => i.showInPicker)}
        onSelect={data => handleSelectIcon(data as CabinetIconData)}
        onCancel={() => setIconPickerOpen(false)}
      />
      <ConfirmationModalComponent />
    </>
  );
};

export default EditProfileCategory;